import { useState, useEffect } from "react";
import { Check, Copy, ExternalLink, Zap, ChevronRight, Terminal, Globe, Bot } from "lucide-react";

const MCP_ENDPOINT = "https://lh-agent-apis.onrender.com/mcp";

type PlatformGuide = {
  id: string;
  name: string;
  summary: string;
  category: "terminal" | "web" | "desktop";
  steps: Array<{ text: string; code?: string }>;
  note?: string;
  documentation: string;
  documentationUrl: string;
};

const categoryIcon = {
  terminal: Terminal,
  web: Globe,
  desktop: Bot,
};

const categoryLabel = {
  terminal: "Terminal",
  web: "Web",
  desktop: "Desktop",
};

const platformGuides: PlatformGuide[] = [
  {
    id: "claude-code",
    name: "Claude Code",
    summary: "Connect from the Claude Code terminal.",
    category: "terminal",
    steps: [
      {
        text: "Run this command in your terminal:",
        code: `claude mcp add --transport http linkedhash ${MCP_ENDPOINT}`,
      },
      { text: "Enter /mcp inside Claude Code." },
      { text: "Select linkedhash and complete authentication." },
    ],
    documentation: "Claude Code documentation",
    documentationUrl: "https://code.claude.com/docs/en/mcp",
  },
  {
    id: "claude-desktop",
    name: "Claude Desktop / web",
    summary: "Add LinkedHash as a remote custom connector.",
    category: "desktop",
    steps: [
      { text: "Go to Customize > Connectors." },
      { text: "Select +, then Add custom connector." },
      {
        text: "Name it LinkedHash and add this MCP endpoint:",
        code: MCP_ENDPOINT,
      },
      { text: "Select Connect and complete authentication." },
    ],
    documentation: "Claude connector documentation",
    documentationUrl:
      "https://support.claude.com/en/articles/11175166-get-started-with-custom-connectors-using-remote-mcp",
  },
  {
    id: "chatgpt",
    name: "ChatGPT",
    summary: "Create a custom MCP app in ChatGPT developer mode.",
    category: "web",
    steps: [
      {
        text: "Enable Developer mode in ChatGPT Settings > Apps > Advanced settings.",
      },
      { text: "Go to Settings > Apps > Create." },
      { text: "Add the LinkedHash MCP endpoint:", code: MCP_ENDPOINT },
      { text: "Scan tools, complete authentication, and select Create." },
    ],
    note: "Custom MCP apps currently require an eligible ChatGPT Business, Enterprise, or Edu workspace.",
    documentation: "ChatGPT MCP app documentation",
    documentationUrl:
      "https://help.openai.com/en/articles/12584461-developer-mode-and-mcp-apps-in-chatgpt",
  },
  {
    id: "codex",
    name: "Codex",
    summary: "Configure LinkedHash in the Codex app or IDE extension.",
    category: "desktop",
    steps: [
      { text: "Open MCP settings and select Open config.toml." },
      {
        text: "Add this Streamable HTTP server configuration:",
        code: `[mcp_servers.linkedhash]\nurl = "${MCP_ENDPOINT}"`,
      },
      {
        text: "Complete OAuth authentication, then use /mcp to verify the connection.",
      },
    ],
    documentation: "Codex MCP documentation",
    documentationUrl: "https://developers.openai.com/codex/mcp",
  },
  {
    id: "codex-cli",
    name: "Codex CLI",
    summary: "Add and authenticate LinkedHash from your terminal.",
    category: "terminal",
    steps: [
      {
        text: "Run this command in your terminal:",
        code: `codex mcp add linkedhash --url ${MCP_ENDPOINT}`,
      },
      {
        text: "Authenticate the server:",
        code: "codex mcp login linkedhash",
      },
      { text: "Enter /mcp in Codex CLI to confirm LinkedHash is active." },
    ],
    documentation: "Codex MCP documentation",
    documentationUrl: "https://developers.openai.com/codex/mcp",
  },
];

function CopyButton({ value, label = "Copy" }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);

  const copyValue = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  };

  return (
    <button
      type="button"
      onClick={copyValue}
      className="inline-flex h-7 items-center gap-1.5 rounded border border-border bg-background px-2.5 text-xs font-medium text-muted-foreground transition-all duration-150 hover:border-accent/40 hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 active:scale-95"
      aria-label={`${label} to clipboard`}
    >
      {copied ? (
        <Check className="size-3.5 text-gain" />
      ) : (
        <Copy className="size-3.5" />
      )}
      <span className="hidden sm:inline">{copied ? "Copied!" : label}</span>
    </button>
  );
}

function CategoryBadge({ category }: { category: PlatformGuide["category"] }) {
  const Icon = categoryIcon[category];
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
      <Icon className="size-2.5" />
      {categoryLabel[category]}
    </span>
  );
}

function useActiveSection(ids: string[]) {
  const [activeId, setActiveId] = useState<string>(ids[0] ?? "");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        }
      },
      { rootMargin: "-20% 0px -70% 0px", threshold: 0 }
    );

    for (const id of ids) {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    }

    return () => observer.disconnect();
  }, [ids]);

  return activeId;
}

export default function ConnectorTab() {
  const allSections = [
    "overview",
    "capabilities",
    "endpoint",
    "connection-guide",
    ...platformGuides.map((g) => g.id),
  ];
  const activeId = useActiveSection(allSections);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="relative flex min-h-full w-full">
      {/* ── MAIN CONTENT ── */}
      <main className="min-w-0 flex-1 pb-24 pr-0 lg:pr-72">

        {/* ── HERO ── */}
        <header id="overview" className="scroll-mt-6 mb-10 border-b border-border pb-10">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-accent/25 bg-accent/10 px-3 py-1">
            <Zap className="size-3 text-accent" />
            <span className="font-mono text-[10px] font-semibold uppercase tracking-widest text-accent">
              LinkedHash MCP
            </span>
          </div>

          <h1 className="mb-3 text-3xl font-bold tracking-tight text-foreground md:text-4xl">
            Connect with MCP
          </h1>
          <p className="max-w-prose text-base leading-7 text-muted-foreground">
            LinkedHash MCP brings live financial intelligence into your AI assistant.
            Research markets, analyze options, monitor opportunities — all without leaving your conversation.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-1.5">
            {platformGuides.map((guide) => (
              <button
                key={guide.id}
                onClick={() => scrollTo(guide.id)}
                className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-xs font-medium text-muted-foreground transition-all hover:border-accent/40 hover:text-accent"
              >
                <ChevronRight className="size-3" />
                {guide.name}
              </button>
            ))}
          </div>
        </header>

        {/* ── CAPABILITIES ── */}
        <section id="capabilities" className="scroll-mt-6 mb-12">
          <div className="mb-1 font-mono text-[10px] font-semibold uppercase tracking-widest text-accent">
            Features
          </div>
          <h2 className="mb-1 text-xl font-semibold tracking-tight text-foreground">
            What LinkedHash enables
          </h2>
          <p className="mb-6 text-sm text-muted-foreground">
            Once connected, your AI assistant gains access to a full suite of financial intelligence tools.
          </p>

          <div className="grid gap-px rounded-lg border border-border bg-border sm:grid-cols-2">
            {[
              { title: "Live Market Data", desc: "Real-time options flow, volatility, Greeks, and price behavior.", icon: "📊" },
              { title: "Integrated Analysis", desc: "Combine technical, fundamental, and macro signals in one view.", icon: "🔬" },
              { title: "Smart Alerts", desc: "Create watches and get notified when your conditions are met.", icon: "🔔" },
              { title: "Conversational Tools", desc: "Use LinkedHash financial tools directly in your AI chat.", icon: "💬" },
            ].map((item, i) => (
              <div
                key={i}
                className="flex gap-3 bg-background p-5 transition-colors hover:bg-card first:rounded-tl-lg sm:[&:nth-child(2)]:rounded-tr-lg sm:[&:nth-child(3)]:rounded-bl-lg last:rounded-br-lg"
              >
                <span className="text-xl leading-none">{item.icon}</span>
                <div>
                  <h3 className="mb-1 text-sm font-semibold text-foreground">{item.title}</h3>
                  <p className="text-xs leading-5 text-muted-foreground">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── MCP ENDPOINT ── */}
        <section id="endpoint" className="scroll-mt-6 mb-12">
          <div className="mb-1 font-mono text-[10px] font-semibold uppercase tracking-widest text-accent">
            Configuration
          </div>
          <h2 className="mb-1 text-xl font-semibold tracking-tight text-foreground">
            MCP Endpoint
          </h2>
          <p className="mb-5 text-sm text-muted-foreground">
            Use this URL when configuring any MCP-compatible platform.
          </p>

          <div className="rounded-lg border border-border bg-card">
            <div className="flex items-center justify-between gap-4 border-b border-border px-4 py-2.5">
              <span className="font-mono text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                LinkedHash MCP Endpoint
              </span>
              <CopyButton value={MCP_ENDPOINT} label="Copy URL" />
            </div>
            <div className="px-4 py-3.5">
              <code className="break-all font-mono text-sm text-foreground">
                {MCP_ENDPOINT}
              </code>
            </div>
          </div>
        </section>

        {/* ── CONNECTION GUIDE ── */}
        <section id="connection-guide" className="scroll-mt-6">
          <div className="mb-1 font-mono text-[10px] font-semibold uppercase tracking-widest text-accent">
            Setup
          </div>
          <h2 className="mb-1 text-xl font-semibold tracking-tight text-foreground">
            Connect your AI agent
          </h2>
          <p className="mb-8 text-sm text-muted-foreground">
            Choose a platform below, then follow the step-by-step instructions to connect
            LinkedHash and authorize access to your financial tools.
          </p>

          <div className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
            {platformGuides.map(({ id, name, summary, category, steps, note, documentation, documentationUrl }) => (
              <article key={id} id={id} className="scroll-mt-6">
                {/* Platform header */}
                <div className="flex items-start gap-3 px-5 pt-6 pb-4">
                  <div className="flex flex-1 flex-col gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-base font-semibold text-foreground">{name}</h3>
                      <CategoryBadge category={category} />
                    </div>
                    <p className="text-sm text-muted-foreground">{summary}</p>
                  </div>
                </div>

                {/* Steps */}
                <div className="px-5 pb-5">
                  <ol className="relative space-y-0">
                    {steps.map((step, index) => (
                      <li key={`${id}-${index}`} className="relative flex gap-4">
                        {index < steps.length - 1 && (
                          <div aria-hidden className="absolute left-[15px] top-8 bottom-0 w-px bg-border" />
                        )}
                        <div className="relative z-10 mt-1 flex size-[30px] shrink-0 items-center justify-center rounded-full border border-border bg-background text-[11px] font-bold tabular-nums text-muted-foreground">
                          {index + 1}
                        </div>
                        <div className="min-w-0 flex-1 pb-5">
                          <p className="pt-1 text-sm leading-6 text-foreground">{step.text}</p>
                          {step.code && (
                            <div className="relative mt-2 overflow-hidden rounded-md border border-border bg-background">
                              <div className="flex items-center justify-between border-b border-border px-3 py-1.5">
                                <div className="flex items-center gap-1.5">
                                  <span className="size-2 rounded-full bg-border" />
                                  <span className="size-2 rounded-full bg-border" />
                                  <span className="size-2 rounded-full bg-border" />
                                </div>
                                <CopyButton value={step.code} label="Copy" />
                              </div>
                              <pre className="overflow-x-auto px-4 py-3 font-mono text-xs leading-relaxed text-foreground">
                                <code>{step.code}</code>
                              </pre>
                            </div>
                          )}
                        </div>
                      </li>
                    ))}
                  </ol>

                  <div className="mt-1 flex flex-col items-start gap-3 pl-[46px]">
                    {note && (
                      <div className="flex gap-2.5 rounded-md border border-warning/30 bg-warning/5 px-3 py-2.5">
                        <span className="shrink-0 text-xs font-semibold text-warning">Note</span>
                        <p className="text-xs leading-5 text-muted-foreground">{note}</p>
                      </div>
                    )}
                    <a
                      href={documentationUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-accent underline-offset-4 transition-colors hover:text-accent/80 hover:underline"
                    >
                      {documentation}
                      <ExternalLink className="size-3.5" />
                    </a>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>
      </main>

      {/* ── SIDEBAR TOC ── */}
      <aside className="hidden lg:block">
        <div className="fixed right-6 top-8 w-56 max-h-[calc(100vh-4rem)] overflow-y-auto">
          <nav aria-label="On this page" className="border-l-2 border-border pl-4">
            <p className="mb-3 font-mono text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
              On this page
            </p>

            <ul className="space-y-0.5">
              {[
                { id: "overview", label: "Overview" },
                { id: "capabilities", label: "Features" },
                { id: "endpoint", label: "MCP Endpoint" },
                { id: "connection-guide", label: "Connect your agent" },
              ].map(({ id, label }) => (
                <li key={id}>
                  <button
                    onClick={() => scrollTo(id)}
                    className={`w-full rounded px-2 py-1.5 text-left text-xs font-medium transition-all duration-150 ${
                      activeId === id ? "text-accent" : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {label}
                  </button>
                </li>
              ))}
            </ul>

            <div className="mb-2 mt-4 border-t border-border pt-4">
              <p className="mb-2 font-mono text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60">
                Platforms
              </p>
              <ul className="space-y-0.5">
                {platformGuides.map(({ id, name, category }) => {
                  const Icon = categoryIcon[category];
                  return (
                    <li key={id}>
                      <button
                        onClick={() => scrollTo(id)}
                        className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs transition-all duration-150 ${
                          activeId === id ? "text-accent" : "text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        <Icon className={`size-3 shrink-0 ${activeId === id ? "text-accent" : "text-muted-foreground/50"}`} />
                        {name}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </nav>
        </div>
      </aside>
    </div>
  );
}
